import React from 'react';
import {
    AiSearchTriggerFloating,
    useAiWidgetConfig,
    useOptionalAiWidgetConfig,
    AiSearchTrigger,
    AiCornerAgent,
} from '@atlassian/csm-ai-search-trigger';
import { useRelayResource } from '@atlassian/help-center-common-util/resource-router';

import { SupportWidgetConfigPreloadedQueryProvider } from '../services/support-widget-config-provider';
import type { supportWidgetConfigProviderQuery } from '../services/support-widget-config-provider/__generated__/supportWidgetConfigProviderQuery.graphql';
import { csmSupportWidgetConfigQueryResource } from '../services/support-widget-config-resource';

export const CSMSupportWidgetConfigProvider = ({ children }: { children: React.ReactNode }) => {
    const { queryReference } = useRelayResource<supportWidgetConfigProviderQuery>(csmSupportWidgetConfigQueryResource);

    if (!queryReference) {
        return null;
    }

    return (
        <SupportWidgetConfigPreloadedQueryProvider queryReference={queryReference}>
            {children}
        </SupportWidgetConfigPreloadedQueryProvider>
    );
};

/**
 * Connects the AiSearchTrigger to the AiWidgetConfigProvider
 */
export const CSMSupportSiteAiSearchTrigger = () => {
    const { supportWidgetConfig } = useAiWidgetConfig();

    return (
        <AiSearchTrigger
            site={supportWidgetConfig.site}
            siteId={supportWidgetConfig.siteId}
            widgetId={supportWidgetConfig.widgetId}
            widgetTheme={supportWidgetConfig.widgetTheme}
        />
    );
};

/**
 * Connects the AiSearchTriggerFloating to the AiWidgetConfigProvider
 */
export const CSMSupportAgentAiSearchTriggerFloating = () => {
    const { supportWidgetConfig } = useAiWidgetConfig();

    return (
        <AiSearchTriggerFloating
            site={supportWidgetConfig.site}
            siteId={supportWidgetConfig.siteId}
            widgetId={supportWidgetConfig.widgetId}
            widgetTheme={supportWidgetConfig.widgetTheme}
        />
    );
};

export const CSMSupportSiteAiCornerAgent = () => {
    const { supportWidgetConfig } = useAiWidgetConfig();

    return (
        <AiCornerAgent
            site={supportWidgetConfig.site}
            siteId={supportWidgetConfig.siteId}
            widgetId={supportWidgetConfig.widgetId}
        />
    );
};

/**
 * Renders either the "central" floating ai search trigger or the "corner" bubble trigger
 * Depending on whether the agent is enabled and user configuration of position
 */
export const CSMSupportAgent = () => {
    const contextValue = useOptionalAiWidgetConfig();

    if (__SERVER__) {
        return null;
    }

    if (!contextValue || !contextValue.supportWidgetConfig.isEnabled) {
        return null;
    }

    if (contextValue.supportWidgetConfig.widgetPosition === 'CORNER') {
        return <CSMSupportSiteAiCornerAgent />;
    }

    return <CSMSupportAgentAiSearchTriggerFloating />;
};
